/**
 * Supplier Card Component
 *
 * Displays a supplier card with its information.
 */

import { Building2, Warehouse, Package, ShoppingCart, Mail, Phone, MapPin, MoreVertical } from 'lucide-react';
import { Button, Badge, Dropdown, DropdownItem, DropdownDivider } from '@/components/ui';
import type { SupplierWithStats } from '@bstock/shared';

interface SupplierCardProps {
  supplier: SupplierWithStats;
  onEdit: (supplier: SupplierWithStats) => void;
  onToggleStatus: (supplier: SupplierWithStats) => void;
  onViewCatalog: (supplier: SupplierWithStats) => void;
}

export function SupplierCard({
  supplier,
  onEdit,
  onToggleStatus,
  onViewCatalog,
}: SupplierCardProps) {
  const address = [supplier.city, supplier.postalCode].filter(Boolean).join(' ');

  return (
    <div
      className={`flex flex-col bg-white rounded-lg border p-4 hover:shadow-md transition-shadow overflow-hidden ${
        !supplier.isActive ? 'opacity-60' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3 min-w-0">
        <div className={`shrink-0 p-2 rounded-lg ${supplier.isInternal ? 'bg-amber-100' : 'bg-primary-100'}`}>
          {supplier.isInternal
            ? <Warehouse className="w-5 h-5 text-amber-600" />
            : <Building2 className="w-5 h-5 text-primary-600" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-neutral-900 truncate" title={supplier.name}>
            {supplier.name}
          </h3>
          {supplier.code && (
            <p className="text-xs text-neutral-500 truncate">Code: {supplier.code}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {supplier.isInternal && (
            <Badge variant="warning">Interne</Badge>
          )}
          <Badge variant={supplier.isActive ? 'success' : 'neutral'}>
            {supplier.isActive ? 'Actif' : 'Inactif'}
          </Badge>
          <Dropdown
            trigger={
              <Button variant="ghost" size="sm" className="p-1">
                <MoreVertical className="w-4 h-4" />
              </Button>
            }
          >
            <DropdownItem onClick={() => onEdit(supplier)}>Modifier</DropdownItem>
            <DropdownItem onClick={() => onViewCatalog(supplier)}>Voir catalogue</DropdownItem>
            {!supplier.isInternal && (
              <>
                <DropdownDivider />
                <DropdownItem
                  onClick={() => onToggleStatus(supplier)}
                  className="text-danger-600"
                >
                  Supprimer
                </DropdownItem>
              </>
            )}
          </Dropdown>
        </div>
      </div>

      {/* Contact Info */}
      <div className="flex-1 space-y-1.5 mb-4 min-w-0">
        {supplier.email && (
          <div className="flex items-center gap-2 text-sm text-neutral-600 min-w-0">
            <Mail className="w-4 h-4 shrink-0 text-neutral-400" />
            <span className="truncate">{supplier.email}</span>
          </div>
        )}
        {supplier.phone && (
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Phone className="w-4 h-4 shrink-0 text-neutral-400" />
            <span>{supplier.phone}</span>
          </div>
        )}
        {address && (
          <div className="flex items-center gap-2 text-sm text-neutral-600 min-w-0">
            <MapPin className="w-4 h-4 shrink-0 text-neutral-400" />
            <span className="truncate">{address}</span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 pt-3 border-t border-neutral-100">
        <div className="flex items-center gap-1.5 text-sm text-neutral-600">
          <Package className="w-4 h-4 shrink-0 text-neutral-400" />
          <span>{supplier.catalogItemsCount} references</span>
        </div>
        {supplier.activeOrdersCount > 0 && (
          <div className="flex items-center gap-1.5 text-sm text-neutral-600">
            <ShoppingCart className="w-4 h-4 shrink-0 text-neutral-400" />
            <span>{supplier.activeOrdersCount} commandes</span>
          </div>
        )}
      </div>
    </div>
  );
}
